import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslations } from '@libs/translations';
import { Points } from '@styles';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { Questions } from '@components/Questions';
import { correctTimeInDate } from './helpers';
import { Card } from './organism/card';

export const WebinarsPage = ({ webinar_cards = {}, webinar_faq = {} }) => {
  const translations = useTranslations();
  const cards = Object.values(webinar_cards?.items || {});
  const [windowWidth, setWindowWidth] = useState(0);
  const cardsData = useMemo(
    () =>
      Object.values(cards || {})
        .filter((item) => item?.form_data?.link.length > 0 || item?.form_data?.webinar_link.length > 0)
        .map((item) => ({ ...item, ...item.form_data }))
        .map((item) => ({ ...item, date: correctTimeInDate(item.date, item.time) })),
    [cards]
  );

  const questionKeys = Object.keys(webinar_faq.items);
  const questionData = questionKeys.map((key) => ({
    question: webinar_faq.items[key].form_data.question,
    answer: webinar_faq.items[key].form_data.answer,
  }));

  const cardsSorted = useMemo(() => {
    const liveWebinars = cardsData.filter((item) => item.link === '').map((item) => ({ ...item, type: 'live' }));
    const records = cardsData.filter((item) => item.link !== '').map((item) => ({ ...item, type: 'record' }));

    return [...liveWebinars, ...records];
  }, [cardsData]);

  const today = new Date();

  useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth);
    }

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Container>
      <Title>{translations.t('site.webinars.title')}</Title>
      <Description dangerouslySetInnerHTML={{ __html: translations.t('site.webinars.description') }} />
      <CardsWrap>
        {cardsSorted.map((item) => (
          <Card data={item} today={today} key={item.id} windowWidth={windowWidth} />
        ))}
      </CardsWrap>
      <Questions
        questionData={questionData}
        titleTranslationKey={'site.webinars.faq.title'}
        descriptionTranslationKey={'site.webinars.faq.description'}
        className={'questions-section'}
      />
    </Container>
  );
};

const Container = styled.div`
  margin: 0 16px;
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;

  @media ${Points.tablet_landscape} {
    max-width: calc(1180px + 28px + 28px);
    margin: 0 auto;
    padding: 0 28px;
  }

  @media ${Points.laptop_wide} {
    padding: 0 0;
    width: 1280px;
    max-width: 1280px;
  }

  @media ${Points.fullhd} {
    width: 1386px;
    max-width: 1386px;
  }

  .questions-section {
    padding-top: 50px;
  }
`;

const CardsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Title = styled.h1`
  margin-top: 55px;
  margin-bottom: 20px;
  color: ${Colors.main_black};
  text-align: center;
  font-size: 36px;
  line-height: 1;
  font-weight: 200;

  @media ${Points.tablet_landscape} {
    width: 606px;
    margin: 50px auto 30px;
    font-size: 48px;
  }

  @media ${Points.laptop_wide} {
    margin: 70px auto 30px;
    width: 830px;
    font-size: 66px;
  }

  @media ${Points.fullhd} {
    width: 1006px;
    font-size: 80px;
    line-height: 0.9;
  }
`;

const Description = styled.p`
  margin-bottom: 60px;
  text-align: center;
  color: ${Colors.graphite};
  font-size: 16px;
  line-height: 1.3;
  font-weight: 400;

  @media ${Points.tablet_landscape} {
    margin: 0 auto 70px;
    width: fit-content;
  }

  @media ${Points.laptop_wide} {
    margin: 0 auto 90px;
  }

  @media ${Points.fullhd} {
    margin: 0 auto 80px;
    font-size: 18px;
  }
`;
